import { useState } from "react";
import ReportHeader, {
  getEosDateStringWithGeAndLt,
} from "../../../../components/Reports/ReportHeader";
import {
  useGeAndLtFromUrl,
  useProcessGroupNameAndMachemetryIds,
} from "../../../../components/Reports/reportUtils";
import LoadingCircle from "reports/components/simple/LoadingCircle";
import PnlBreakdownGetter from "../../../../getters/pnlBreakdownGetter/PnlBreakdownGetter";
import {
  PnlBreakdown,
  PnlBreakdownFiltersEnum,
} from "../../../../getters/pnlBreakdownGetter/pnlBreakdownTypes";
import PciukWeeklySkuChart from "./PciukWeeklySkuChart";
import { getPciukMetrics } from "../eos/pciukEosUtils";
import { round, startCase } from "lodash";
import PrintableRechart from "../../../../components/Reports/PrintableRechart";
import { useGetIsOperatingSystemWindows } from "utils/customHooks/useGetIsOperatingSystemWindows";
import { PrintableReportStyles } from "../../../../components/Reports/printableReportContentStyles";
import { brightBlue, unplanned } from "../../../../constants/colors";
import MetricsExplainer from "../../../../components/Reports/MetricsExplainer/MetricsExplainer";
import { useMediaQuery } from "@mui/material";
import { BREAKPOINT_MOBILE } from "reports/constants/constants";
import SkuAndMetricReport from "reports/components/Reports/SkuAndMetricReport";

const LINE_EFF = "lineEfficiency";
const ENG_DOWNTIME = "engineeringDowntime";

export type SkuChartDataType = {
  sku: string;
  metric: number;
}[];

/**
 * Calculates engineeringDowntime and lineEfficiency per pnlBreakdown object
 *
 * @param {{
 *   pnlBreakdowns: PnlBreakdown[];
 *   metricName: string;
 * }} {
 *   pnlBreakdowns,
 *   metricName
 * }
 * @return {*} { sku: string; metric: number }[]
 */
const getChartData = ({
  pnlBreakdowns,
  metricName,
  isMobileOrSmaller,
}: {
  pnlBreakdowns: PnlBreakdown[];
  metricName: string;
  isMobileOrSmaller: boolean;
}) => {
  return (
    pnlBreakdowns
      .map(pnlBreakdown => {
        const metric =
          metricName === LINE_EFF
            ? pnlBreakdown.oeePercent
            : getPciukMetrics(pnlBreakdown)[metricName];

        const sku = isMobileOrSmaller
          ? pnlBreakdown.skuDescription
          : `${pnlBreakdown.skuDescription}\n(${pnlBreakdown.sku})`;
        return {
          sku,
          metric: metric !== null ? round(metric, 1) : metric,
        };
      })
      .filter(({ metric }) => metric !== null)
      // Sorts from highest to lowest percent
      .sort((a, b) => b.metric - a.metric) as SkuChartDataType
  );
};

export default function PciukWeeklySku() {
  const isWindows = useGetIsOperatingSystemWindows();
  const { machemetryIds } = useProcessGroupNameAndMachemetryIds({});
  const { ge, lt } = useGeAndLtFromUrl();
  const dateString = getEosDateStringWithGeAndLt(ge, lt);

  const [pnlBreakdownsBySKU, setPnlBreakdownsBySKU] = useState(
    [] as PnlBreakdown[]
  );

  const isMobileOrSmaller = useMediaQuery(
    `(max-width: ${BREAKPOINT_MOBILE}px)`
  );

  const ChartContent = (metricName: string, color: string) => width => {
    const chartData = getChartData({
      pnlBreakdowns: pnlBreakdownsBySKU,
      metricName,
      isMobileOrSmaller,
    });

    return (
      <PciukWeeklySkuChart
        {...{ width, color, chartData }}
        metricTitle={
          metricName === LINE_EFF ? "Line Efficiency" : "Eng. Downtime"
        }
      />
    );
  };

  return (
    <>
      <PnlBreakdownGetter
        range={{ ge, lt }}
        machemetryIds={machemetryIds}
        setPnlBreakdownData={setPnlBreakdownsBySKU}
        filter={PnlBreakdownFiltersEnum.sku}
        excludeNullFilterValue={true}
      />
      {pnlBreakdownsBySKU && pnlBreakdownsBySKU.length > 0 ? (
        <>
          {[
            { metricName: LINE_EFF, color: brightBlue },
            { metricName: ENG_DOWNTIME, color: unplanned },
          ].map(({ metricName, color }) => {
            return (
              <div key={metricName}>
                <ReportHeader
                  title="PCI - Hay-On-Wye, UK"
                  subheader={`SKU Performance - ${startCase(metricName)} %`}
                  dateString={dateString}
                />
                <PrintableReportStyles $isWindows={isWindows}>
                  <div style={{ marginBottom: "1em" }}>
                    <PrintableRechart
                      chart={ChartContent(metricName, color)}
                      containerWidthRatio={0.9}
                      printedChartWidthRatio={0.5}
                    />
                    <div style={{ marginLeft: "1em" }}>
                      <MetricsExplainer
                        metricsExplainer={[
                          {
                            name: startCase(metricName),
                            nameTranslated: startCase(metricName),
                          },
                        ]}
                      />
                    </div>
                  </div>
                </PrintableReportStyles>
              </div>
            );
          })}
          <SkuAndMetricReport title="PCI - Hay-On-Wye, UK" />
        </>
      ) : (
        <LoadingCircle />
      )}
    </>
  );
}
